import React from 'react'
import { Link } from 'react-router-dom'
import { TableFooter, TableRow, TableCell } from '@mui/material'
import Pagination from '@/Components/Elements/Pagination'
import ItemCounter from '@/Components/Elements/ItemCounter'
import LoadingDefault from '@/Components/Elements/Loading/LoadingDefault'
import { BadgeTag, CountDot } from '@/Components/Elements/StyledComponents/Tags'
import {
    aggregateTableCellSx,
    aggregateTableRowSx,
} from '@/Components/Elements/StyledComponents/styles'
import {
    formatDateTimeValue,
    getDateTimeLocale,
} from '../../../DisplayItems/DateTimeField/formatDateTimeValue'
import {
    formatAmountValue,
    getAmountLocale,
} from '../../../DisplayItems/formatAmountValue'

interface DossierTableFooterProps {
    loadingContent: any
    results: any
    pager: any
    actionBtns: any[]
    kedo: any
    isCustomActionButton: (item: any) => boolean
    renderAggregateField: (item: any) => React.ReactNode
    onChangePage: (page: number) => void
}

const DefDossierListTableFooter: React.FC<DossierTableFooterProps> = ({
    loadingContent,
    results,
    pager,
    actionBtns,
    kedo,
    isCustomActionButton,
    renderAggregateField,
    onChangePage,
}) => {
    if (!results || !results.dossiers || results.dossiers.length <= 0)
        return null

    const listItems = results.items.filter(
        (item: any) => item.view === 'list' && !isCustomActionButton(item)
    )

    const extraColumns = actionBtns.length > 0 ? 4 : 3
    if (loadingContent) {
        return (
            <TableFooter>
                <TableRow
                    sx={{
                        '&& .MuiTableCell-root': {
                            verticalAlign: 'top',
                            py: 1,
                        },
                    }}
                >
                    <TableCell
                        colSpan={listItems.length + extraColumns}
                        sx={{ textAlign: 'center' }}
                    >
                        <LoadingDefault />
                    </TableCell>
                </TableRow>
            </TableFooter>
        )
    }
    return (
        <TableFooter>
            <TableRow sx={aggregateTableRowSx} className="tr-aggr">
                <TableCell sx={{ width: '3%' }} />
                <TableCell sx={{ textAlign: 'center' }} />
                {listItems.map((listItem, lIndex) => (
                    <TableCell key={lIndex} sx={aggregateTableCellSx}>
                        {renderAggregateField(listItem)}

                        {/* Link badges */}
                        {listItem?.def_dossier_link &&
                            results?.aggregations?.[listItem.id] && (
                                <div className="aggr-result">
                                    {results.aggregations?.[listItem.id].map(
                                        (aggrItem: any, aggrIndex: number) => (
                                            <Link
                                                key={aggrIndex}
                                                to={`/contentdossier/${aggrItem.id}`}
                                            >
                                                {aggrItem.summary}{' '}
                                                <CountDot>
                                                    {aggrItem.total}
                                                </CountDot>
                                            </Link>
                                        )
                                    )}
                                </div>
                            )}

                        {/* Field aggregation based on type */}
                        {listItem?.def_dossier_def_field &&
                            results?.aggregations?.[listItem.id] !== null &&
                            results?.aggregations?.[listItem.id] !==
                                undefined && (
                                <div className="aggr-result">
                                    {/* Date type */}
                                    {listItem.def_dossier_def_field.def_field
                                        .type === 'date' &&
                                        formatDateTimeValue(
                                            results.aggregations[listItem.id],
                                            getDateTimeLocale(kedo),
                                            Boolean(
                                                listItem.def_dossier_def_field
                                                    .def_field.settings
                                                    ?.withtime
                                            )
                                        )}

                                    {/* Time type */}
                                    {listItem.def_dossier_def_field.def_field
                                        .type === 'time' &&
                                        results.aggregations[listItem.id]}

                                    {/* Amount type */}
                                    {listItem.def_dossier_def_field.def_field
                                        .type === 'amount' &&
                                        formatAmountValue(
                                            results.aggregations[listItem.id],
                                            listItem.def_dossier_def_field
                                                .def_field.settings,
                                            getAmountLocale(kedo)
                                        )}

                                    {/* List type */}
                                    {listItem.def_dossier_def_field.def_field
                                        .type === 'list' &&
                                        listItem.def_dossier_def_field.def_field.def_field_lists
                                            .filter((defFieldListItem: any) =>
                                                results.aggregations[
                                                    listItem.id
                                                ].find(
                                                    (selItem: any) =>
                                                        selItem.id ===
                                                        defFieldListItem.id
                                                )
                                            )
                                            .map(
                                                (
                                                    defFieldListItem: any,
                                                    dIndex: number
                                                ) => {
                                                    const total =
                                                        results.aggregations[
                                                            listItem.id
                                                        ].find(
                                                            (selItem: any) =>
                                                                selItem.id ===
                                                                defFieldListItem.id
                                                        )?.total ?? 0

                                                    if (!total) return null

                                                    const label =
                                                        kedo.translateItem(
                                                            defFieldListItem,
                                                            'def_field_list'
                                                        )

                                                    return (
                                                        <BadgeTag
                                                            key={dIndex}
                                                            color={
                                                                defFieldListItem.color ||
                                                                undefined
                                                            }
                                                            textColor={
                                                                defFieldListItem.color
                                                                    ? '#fff'
                                                                    : undefined
                                                            }
                                                            size="small"
                                                        >
                                                            {label}
                                                            <CountDot inverted>
                                                                {total}
                                                            </CountDot>
                                                        </BadgeTag>
                                                    )
                                                }
                                            )}
                                </div>
                            )}
                    </TableCell>
                ))}
                <TableCell colSpan={extraColumns - 2} />
            </TableRow>

            <TableRow
                sx={{
                    '&& .MuiTableCell-root': {
                        verticalAlign: 'top',
                        borderTop: 1,
                        borderTopColor: 'divider',
                        py: 1,
                    },
                }}
            >
                <TableCell colSpan={results.items.length + extraColumns}>
                    <Pagination pager={pager} onChangePage={onChangePage} />
                    <ItemCounter pager={pager} />
                </TableCell>
            </TableRow>
        </TableFooter>
    )
}

export default DefDossierListTableFooter
